'use client'

import { cn } from '@mntn-dev/ui-utilities'

import { themeTextColorMap } from '@mntn-dev/ui-theme'
import { useMemo } from 'react'
import { type TestIds, getTestProps } from '../../../utils/index.ts'
import { Decorator } from '../../decorator/decorator.tsx'
import { Icon, type IconName, type IconProps } from '../../icon/index.ts'
import { NavigationSelectIndicator } from './navigation-select-indicator.tsx'

type NavigationIconItemProps = Readonly<
  {
    iconName: IconName
    unreadNotificationCount?: number
    selected: () => boolean
    onClick: () => void
    title?: string
    disabled?: boolean
  } & TestIds
> &
  Pick<IconProps, 'fill'>

const NavigationIconItem = ({
  iconName,
  unreadNotificationCount = 0,
  selected,
  disabled,
  onClick,
  title,
  dataTestId,
  dataTrackingId,
  fill,
}: NavigationIconItemProps) => {
  const isSelected = useMemo(() => selected(), [selected])

  const icon = useMemo(
    () => (
      <Icon
        name={iconName}
        disabled={disabled}
        fill={fill}
        size="xl"
        color={isSelected ? 'info' : 'secondary'}
      />
    ),
    [disabled, fill, iconName, isSelected]
  )

  return (
    <li className="group relative">
      <button
        disabled={disabled}
        onClick={onClick}
        className="flex h-16 w-16 flex-col items-center justify-center"
        type="button"
        {...getTestProps({ dataTestId, dataTrackingId })}
      >
        <div className={cn({ 'drop-shadow-glow-blue-light': selected })}>
          {unreadNotificationCount > 0 ? (
            <Decorator
              size="xs"
              offsetX="-1"
              offsetY="-1"
              anchor="top-right"
              value={unreadNotificationCount}
            >
              {icon}
            </Decorator>
          ) : (
            icon
          )}
        </div>
        {isSelected && <NavigationSelectIndicator />}
      </button>
      {title && (
        <span
          className={`absolute -bottom-1 left-1/2 -translate-x-2/4 whitespace-nowrap text-[8px] font-bold uppercase ${themeTextColorMap.secondary} overflow-none opacity-0 transition-all duration-300 ease-out group-hover:opacity-100`}
        >
          {title}
        </span>
      )}
    </li>
  )
}

export { NavigationIconItem, type NavigationIconItemProps }
